import kebabCase from 'lodash/kebabCase'
import router from '@/router'
import type {
  OnboardingResponse,
  SetupSelectionInput,
} from '@onxmaps/supergraph'

import {
  ONBOARDING_MAPPED_API_STEPS,
  DEFAULT_ONBOARDING_STEPS,
  REQUIRED_ONBOARDING_STEPS,
} from '@/onboarding/lib/steps'

import { log } from '@/services/logging-service'
import subscriptionService from '@/services/subscription-service'
import { isActive } from '@/services/feature-flag-service'

import { saveItem, getItem } from '@/plugins/storage'
import { getApolloClient } from '@/lib/apollo-client'
import gql from 'graphql-tag'
import device from '@/lib/device'
import { captureException } from '@sentry/vue'
import {
  LocalOnboardingStep,
  isLocalOnboardingStep,
  type ApiOnboardingStep,
} from '@/onboarding/types'
import type {
  NavigationGuard,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  RouteRecordNormalized,
} from 'vue-router'
import { Vertical } from '@/types/verticals'
import marketingEventService from '@/services/marketing-event-service'
import { getUserId } from '@/services/identity-client'
import { onboardingLog } from '@/onboarding/lib/log'
import {
  onboardingStorageKeyMap,
  setupSelectionStore,
} from '@/onboarding/services/setup-selection-storage'
import { ref } from 'vue'

type OnboardingResponseType = {
  onboardingPages: Array<
    Pick<OnboardingResponse['onboardingPages'][number], 'key'> & {
      body?: { analyticsId?: string }[]
    }
  >
}

type NamedRouteLocation = Exclude<RouteLocationRaw, string>

export const neededOnboardingSteps = ref<LocalOnboardingStep[]>([])
export const currentOnboardingStep = ref<LocalOnboardingStep | null>(null)

/**
 * Used to force a skip of the onboarding steps, in case stakeholders
 * want to link to that map without immediately redirecting
 */
const SKIP_PARAM = 'skip_onboarding'
/**
 * Used to skip via local storage/feature flag, for e2e testing
 */
export const ONBOARDING_SKIP_FLAG = 'skip_onboarding'

/**
 * Initialize onboarding
 */
export const initOnboardingPages = async () => {
  const configSteps: string[] = []
  const neededSteps: LocalOnboardingStep[] = []

  // delete any submitted steps if
  // debug_onboarding employee setting is on
  const isDebug = isActive('debug_onboarding')

  if (subscriptionService.isEmployee() && isDebug) {
    saveItem(`fishSubmittedOnboardingSteps`, [])
  }

  try {
    if (!isActive('skip_onboarding')) {
      const fallbackSteps = DEFAULT_ONBOARDING_STEPS['fish'].map((step) => ({
        key: step,
        analyticsId: undefined,
      }))

      let apiPages
      if (isDebug) {
        apiPages = fallbackSteps
      } else {
        apiPages = (await getOnboardingPages()) ?? fallbackSteps
      }

      // Build storage key map from API response
      onboardingStorageKeyMap.clear()
      apiPages.forEach((page) => {
        // In debug mode, keys are already local step keys — skip API mapping
        const localStep = isDebug
          ? page.key
          : (ONBOARDING_MAPPED_API_STEPS['fish'][
              page.key as ApiOnboardingStep
            ] ?? kebabCase(page.key))
        onboardingStorageKeyMap.set(localStep, page.analyticsId ?? page.key)
      })
      onboardingLog(
        'Built storage key map',
        Object.fromEntries(onboardingStorageKeyMap)
      )

      const apiSteps = apiPages.map((p) => p.key)
      /** Map api steps to local configured identifier eg hunt activity_selector -> species-selector */
      const mappedApiSteps = apiSteps.map(
        (step) =>
          ONBOARDING_MAPPED_API_STEPS['fish'][step as ApiOnboardingStep] ?? step
      )

      configSteps.push(...mappedApiSteps)
    }
  } catch (e) {
    // Do nothing, we will check required steps via logic below
  }

  /**
   * Regardless of API interaction, always check the required steps to see if needed
   */
  const niceConfigSteps = Array.from(
    new Set([
      ...configSteps.map((cs) => kebabCase(cs)),
      ...REQUIRED_ONBOARDING_STEPS['fish'],
    ])
  )

  /** Need subscriptions loaded to verify steps */
  await subscriptionService.subscriptionsReadyPromise

  niceConfigSteps.forEach((cStep) => {
    if (isValidFullPageStep(cStep)) {
      neededSteps.push(cStep)
    }
  })

  // set first (current) step
  if (neededSteps.length) {
    setCurrentOnboardingStep(neededSteps[0])
  } else {
    setCurrentOnboardingStep(null)
  }

  // set all needed steps
  setNeededOnboardingSteps(neededSteps)
}

/**
 * * Start a trial if the user hasn't had any for this vertical before.
 *  * Retrieve needed onboarding steps and return a redirect to
 * onboarding if there are steps to complete.
 * * Will skip redirecting if the user is on their way to anywhere other than landing
 * on the map. Exception is state selection.
 */
export const onboardIfNewUser = async (
  {
    from,
    to,
    app,
    checkStateSelection = false,
    startTrial = true,
  }: {
    from?: RouteLocationNormalizedLoaded
    to?: RouteLocationNormalized
    app: Vertical.Fish
    checkStateSelection?: boolean
    startTrial?: boolean
  } = {
    startTrial: true,
    checkStateSelection: false,
    app: Vertical.Fish,
  }
): Promise<ReturnType<NavigationGuard>> => {
  onboardingLog('Checking onboarding status...')
  /**
   * Prevent redirect:
   * - if is an employee
   * - feature flag set
   * - in case of loop (from is onboarding)
   * - deep link
   * - skip param
   *
   * Still start a trial if none have been used UNLESS skipping via feature flag
   * Check state selection if this isn't due to feature flag skip
   */
  await subscriptionService.subscriptionsReadyPromise
  if (subscriptionService.isEmployee() && !isActive('debug_onboarding')) {
    onboardingLog(
      'Employee account found - skipping onboarding. To test onboarding enable `debug_onboarding` in settings.'
    )
    return
  }
  const shouldStartTrial =
    startTrial && !subscriptionService.hasActiveSubscription()
  const skipOnboarding = isActive('skip_onboarding')

  const onboardingRoute = {
    name: `${app}-onboarding`,
    query: { ...to?.query, after_login: to?.fullPath },
  }

  const hasValidOnboardingRoute = hasValidRoute(onboardingRoute)

  if (
    skipOnboarding ||
    from?.name?.toString().includes('onboarding') ||
    (to?.name && to.name !== `${app}-map`) ||
    Object.keys(to?.query ?? {}).includes(SKIP_PARAM) ||
    !hasValidOnboardingRoute
  ) {
    // if skipping onboarding, just update subscriptions
    if (skipOnboarding) {
      onboardingLog('Skipping onboarding via flag...')
      await subscriptionService.fetchStates()
      return
    } else if (shouldStartTrial) {
      onboardingLog('Skipping onboarding, but starting a trial...')
      // otherwise, start a first trial if applicable AND update subscriptions
      await startFirstTrialAndSyncSubscriptions()
    }
    if (checkStateSelection) {
      onboardingLog('Checking state selection status')
      const stateSelectionCompleted = await verifyHuntStatesSelected()
      if (!stateSelectionCompleted) {
        onboardingLog('User needs state selection')
        return onboardingRoute
      }
    }
    onboardingLog('No onboarding needed')
    return
  }

  if (shouldStartTrial) {
    onboardingLog('Starting trial')
    await startFirstTrialAndSyncSubscriptions()
  }
  const [, stateSelectionCompleted] = await Promise.all([
    initOnboardingPages(),
    checkStateSelection ? verifyHuntStatesSelected() : true,
  ])

  /**
   * If user needs onboarding or state selection, send there
   * and preserve query parameters, add current destination to after_login
   */
  if (onboardingNeeded() || !stateSelectionCompleted) {
    onboardingLog('Onboarding needed, redirecting', { stateSelectionCompleted })
    return onboardingRoute
  } else if ((await hasOnboardingSession()) && !onboardingNeeded()) {
    onboardingLog('no onboarding needed, active session. Completing onboarding')
    // User has no steps left but was in onboarding previously in this session
    submitOnboardingComplete()
    return
  }
  onboardingLog('No onboarding needed')
}

/**
 * Wrapper to allow using as a nav guard in array easily
 *
 * This is to be used as a curried function because calling it directly in the routers leads to test
 * failures when the routers are included in a code path in the test. This puts them 1 layer away so the error
 * is avoided.
 */
export const createNewUserNavigationGuard = ({
  app,
  checkStateSelection,
  startTrial,
}: {
  app: Vertical.Fish
  checkStateSelection?: boolean
  startTrial?: boolean
}) => {
  return (to?: RouteLocationNormalized, from?: RouteLocationNormalizedLoaded) =>
    onboardIfNewUser({ app, checkStateSelection, startTrial, to, from })
}

/**
 * Do we have onboarding steps that are still needed?
 *
 * @note requires `initOnboardingPages` to be run first in order to fetch steps
 * @see initOnboardingPages
 */
export const onboardingNeeded = () => {
  return neededOnboardingSteps.value?.length > 0
}

/**
 * Set current onboarding step
 */
export const setCurrentOnboardingStep = (step: LocalOnboardingStep | null) =>
  (currentOnboardingStep.value = step)

/**
 * Set needed onboarding steps
 * @param steps - array of needed steps
 */
export const setNeededOnboardingSteps = (steps: LocalOnboardingStep[]) => {
  neededOnboardingSteps.value = steps
}

/**
 * Update onboarding steps based on land selection
 * @param landSelection - array of selected land types
 */
export const LandSelection = ['private', 'public', 'skip'] as const
export type LandSelection = (typeof LandSelection)[number]
export const updateStepsBasedOnLandSelection = (
  landSelection: LandSelection[]
) => {
  const currentSteps = neededOnboardingSteps.value
  const landSelectorIndex = currentSteps.indexOf(
    LocalOnboardingStep.LandSelector
  )

  if (landSelectorIndex === -1) {
    saveItem('huntAreaMapPromptDismissed', false)
    onboardingLog('LandSelector not found in current flow')
    return
  }

  // Remove HuntAreas step if it exists
  const newSteps: LocalOnboardingStep[] = currentSteps.filter(
    (step) => step !== LocalOnboardingStep.HuntAreas
  )

  // Add HuntAreas after LandSelector if 'private' was selected
  if (landSelection.includes('private')) {
    newSteps.splice(landSelectorIndex + 1, 0, LocalOnboardingStep.HuntAreas)
    saveItem('huntAreaMapPromptDismissed', false)
  } else {
    saveItem('huntAreaMapPromptDismissed', true)
  }

  setNeededOnboardingSteps(newSteps)
}

/**
 * Add onboarding step which the user has seen/submitted data for
 * to the feature flag array if does not exist in it yet
 * @param step - step to be added
 */
export const saveToSubmittedSteps = (step: LocalOnboardingStep) => {
  const app = Vertical.Fish
  const submittedSteps = getItem(
    `${app}SubmittedOnboardingSteps`
  ) as LocalOnboardingStep[]
  if (!submittedSteps.includes(step)) {
    const newSubmittedSteps = [...submittedSteps, step]
    saveItem(`${app}SubmittedOnboardingSteps`, newSubmittedSteps)
  }
}

/**
 * Check if the router contains the given route as a possible option
 */
export const hasValidRoute = (route: NamedRouteLocation) => {
  const allRoutes: RouteRecordNormalized[] = router.getRoutes()
  if (route && typeof route === 'object' && 'name' in route && route.name) {
    return allRoutes.some((r) => r.name === route.name)
  }
  return false
}

/**
 * Check if step has a valid child route based of vertical base route
 * @param step - config Step
 */
export const hasOnboardingStepRoute = (step: LocalOnboardingStep) => {
  const baseRoute = router.resolve({
    name: `fish-onboarding`,
  })
  return baseRoute.matched[baseRoute.matched.length - 1].children.some(
    ({ name }) => name === `fish-onboarding-${step}`
  )
}

/**
 * Filter function to determine valid full-page onboarding step
 * 1. step is in onboarding lib
 * 2. step has not been seen (submittedSteps) by user before
 * 3. step has a valid route in router
 */
const isValidFullPageStep = (step: string): step is LocalOnboardingStep => {
  // Exception for debug mode
  if (isActive('debug_onboarding')) {
    return true
  }

  const app = 'fish'
  const submittedSteps =
    getItem<LocalOnboardingStep[]>(`${app}SubmittedOnboardingSteps`) || []

  if (step === LocalOnboardingStep.StateSelector) {
    // If the user has already selected their states
    const hasSelectedTheirStates = userHasValidStatesCount()

    return (
      !hasSelectedTheirStates &&
      isLocalOnboardingStep(step) &&
      hasOnboardingStepRoute(step)
    )
  }

  /**
   * Don't show AS step if:
   * - User already has a subscription
   * - User has already seen an AS offer
   */
  if (step === LocalOnboardingStep.Autosubscribe) {
    return (
      (subscriptionService.isTrial() || subscriptionService.isBasic()) &&
      !getItem(`fishSeenAutosubscribe`)
    )
  }

  return (
    isLocalOnboardingStep(step) &&
    !submittedSteps.includes(step) &&
    hasOnboardingStepRoute(step)
  )
}

/**
 * Utilizes the onboardingResponse query to pull keys
 * Does not fully leverage the `OnboardingResponse` type
 * @future fully utilize the capabilities of this schema. This is just to
 * simplify split configuration in the meantime.
 */
export const getOnboardingPages = async () => {
  try {
    const client = getApolloClient()
    const { data, error } = await client.query<{
      onboardingResponse: OnboardingResponseType
    }>({
      query: gql`
        query OnboardingPagesQuery {
          onboardingResponse {
            onboardingPages {
              key
              body {
                ... on OnboardingSelection {
                  analyticsId
                }
              }
            }
          }
        }
      `,
    })
    if (data && !error) {
      return data.onboardingResponse.onboardingPages.map((p) => ({
        key: p.key,
        analyticsId: p.body?.find((b) => b.analyticsId)?.analyticsId,
      }))
    }
    throw new Error(`Error retrieving onboarding pages ${error}`)
  } catch (e) {
    log('get_onboarding_steps_failed', { error: e })
    return
  } finally {
    /**
     * Because this _could_ have the side effect of changing subscriptions, we need to
     * re-fetch these after requesting onboarding steps
     */
    await subscriptionService.syncSubscriptions()
  }
}

export const submitOnboardingComplete = async () => {
  try {
    marketingEventService.publish('onboarding_flow_completed')
    clearOnboardingSession()
    /**
     * Skip all GQL related operations for debug mode
     */
    if (isActive('debug_onboarding')) {
      return
    }
    device.checkDeviceID()
    const deviceID = device.deviceID
    const client = getApolloClient()
    const selections = setupSelectionStore.getAll()
    const setupSelection: SetupSelectionInput = { selections }
    onboardingLog('Submitting onboarding complete', { setupSelection })
    const { data, errors } = await client.mutate({
      mutation: gql`
        mutation CreateOnboardingResult(
          $result: Boolean!
          $deviceID: String!
          $setupSelection: SetupSelectionInput
        ) {
          createOnboardingResult(
            result: $result
            deviceID: $deviceID
            setupSelection: $setupSelection
          ) {
            onboardingComplete
          }
        }
      `,
      variables: {
        deviceID,
        result: true,
        setupSelection,
      },
    })

    if ((!errors || !errors.length) && data) {
      return data.createOnboardingResult.onboardingComplete
    }
    throw new Error(`Error submitting onboarding result ${errors}`)
  } catch (e) {
    captureException(e, {
      tags: {
        onboarding: 'onboarding_complete',
      },
    })
  }
}

/**
 * Checks the user membership level and selected-states to determine if it has the correct number selected
 * @returns Whether the user has the proper no. of states selected. Defaults to true
 */
export const userHasValidStatesCount = () => {
  const subscribedStates = subscriptionService.getHuntStates()

  /**
   * Catch any sort of downgrade/upgrade for users that need to re-select their states
   * 1. require that only 1 state is selected for premium and trial users
   * 2. require that only 2 states are selected for premium multi-state users
   */
  const isInvalidPremium =
    (subscriptionService.isPremium() || subscriptionService.isPremiumTrial()) &&
    subscribedStates.length !== 1

  if (isInvalidPremium) {
    return false
  }
  // Premium multi-state should only be subscribed to 2 states
  const isInValidPremiumMultiState =
    subscriptionService.isPremiumMultiState() && subscribedStates.length !== 2

  if (isInValidPremiumMultiState) {
    return false
  }

  const isInvalidElite =
    (subscriptionService.isElite() || subscriptionService.isEliteTrial()) &&
    subscribedStates.length < 1

  if (isInvalidElite) {
    return false
  }

  /*
    We want to default return true otherwise we will cause a redirect loop
    for users that aren't accounted for here
  */
  return true
}

/**
 * Check if the user has selected the required number of states for their membership
 *
 * @returns Whether the user has the required no. of states selected
 */
export const verifyHuntStatesSelected = () => {
  return true
}

const startFirstTrialAndSyncSubscriptions = async () => {
  try {
    const startedTrial = await subscriptionService.startTrialIfNoneUsed()

    if (!startedTrial) {
      await subscriptionService.syncSubscriptions()
    }
  } catch (e) {
    /**
     * If this errors, just move on. Could be because the user
     * started this membership with a purchase and has used no trials
     * but has an active membership.
     */
  }
}

export const startOnboardingSession = async () => {
  window.sessionStorage.setItem(
    `onboarding-fish`,
    (await getUserId()) as string
  )
}

export const hasOnboardingSession = async () => {
  const session = window.sessionStorage.getItem(`onboarding-fish`)
  if (session === (await getUserId())) {
    return true
  }
  clearOnboardingSession()
}

export const clearOnboardingSession = () => {
  window.sessionStorage.removeItem(`onboarding-fish`)
}

/**
 * Completes onboarding and updates remote when the user completes
 * a purchase with an active onboarding session. Only completes onboarding
 * if the user has no steps remaining after the purchase is completed.
 */
export const completeOnboardingOnPurchase = async () => {
  await initOnboardingPages()
  if (!onboardingNeeded()) {
    if (await hasOnboardingSession()) {
      submitOnboardingComplete()
    }
    clearOnboardingSession()
  }
}
